import { Invite } from '~/repositories/InviteRepository';
import { Factor, PlatformSettings, UserRepository } from '~/repositories/UserRepository';
import { useSessionStore } from '~/stores/Session';
import type { PlatformRoleResponseObject } from '~/typings/API';

import { BaseModel } from './BaseModel';

export type UserType = {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber: string;
  emailState: string;
  factors: Factor[];
  dashboardDisplayMode: string;
  defaultPlatformId: string;
  platformSettings?: PlatformSettings;
  platformRoles?: (PlatformRoleResponseObject & { platformName: string; isLiveEnabled: boolean })[];
  platformInvites?: Invite[];
};

export type UpdateUser = {
  firstName?: string;
  lastName?: string;
  email?: string;
  phoneNumber?: string;
  defaultPlatformId?: string;
};

export class UserModel extends BaseModel {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  factors: Factor[];
  phoneNumber: string;
  emailState: string;
  dashboardDisplayMode: string;
  defaultPlatformId: string;
  platformSettings?: PlatformSettings;
  platformRoles: (PlatformRoleResponseObject & { platformName: string; isLiveEnabled: boolean })[];
  platformInvites: Invite[];

  constructor(attrs: UserType) {
    super();
    this.id = attrs.id;
    this.firstName = attrs.firstName;
    this.lastName = attrs.lastName;
    this.email = attrs.email;
    this.phoneNumber = attrs.phoneNumber;
    this.emailState = attrs.emailState;
    this.factors = attrs.factors;
    this.dashboardDisplayMode = attrs.dashboardDisplayMode;
    this.defaultPlatformId = attrs.defaultPlatformId;
    this.platformSettings = attrs.platformSettings;
    this.platformRoles = attrs.platformRoles || [];
    this.platformInvites = attrs.platformInvites || [];
  }

  get isEmailVerified() {
    return this.emailState === 'VERIFIED';
  }

  get isEmailPending() {
    return this.emailState === 'VERIFY_IN_PROGRESS';
  }

  get getPlatformSettings() {
    return this.platformSettings;
  }

  async createVerifyEmail() {
    await UserRepository.createVerifyEmail();
    this.emailState = 'VERIFY_IN_PROGRESS';

    const sessionStore = useSessionStore.getState();
    sessionStore.setCurrentUser({ ...this });

    return this;
  }

  async verifyEmail(code: string) {
    return UserRepository.verifyEmail(code).then(() => {
      this.emailState = 'VERIFIED';
      const sessionStore = useSessionStore.getState();
      sessionStore.setCurrentUser({ ...this });
    });
  }

  async updatePlatformSettings(request: Partial<PlatformSettings>) {
    const currentSettings = { ...this.platformSettings };

    const pinnedBankAccounts =
      request.pinnedBankAccounts !== undefined
        ? [...new Map(request.pinnedBankAccounts.map((item) => [item.bankAccountId, item])).values()]
        : currentSettings.pinnedBankAccounts;

    const transferSnapshots =
      request.transferSnapshots !== undefined
        ? [...new Map(request.transferSnapshots.map((item) => [item.name, item])).values()]
        : currentSettings.transferSnapshots;

    const mergedSettings = {
      ...request,
      pinnedBankAccounts,
      transferSnapshots,
    };

    const newSettings = structuredClone(mergedSettings);

    this.platformSettings = newSettings;

    return UserRepository.updatePlatformSettings(newSettings).then((response) => {
      if (this.platformSettings && response?.pinnedBankAccounts) {
        this.platformSettings.pinnedBankAccounts = response.pinnedBankAccounts;
      }
      if (this.platformSettings && response?.transferSnapshots) {
        this.platformSettings.transferSnapshots = response.transferSnapshots;
      }

      return response;
    });
  }

  async update(attrs: UpdateUser, reCaptchaToken?: string) {
    if (attrs.email && this.email !== attrs.email) {
      setTimeout(() => {
        this.emailState = 'NOT_VERIFIED';
      }, 0);
    }
    if (attrs.firstName) {
      this.firstName = attrs.firstName;
    }
    if (attrs.lastName) {
      this.lastName = attrs.lastName;
    }
    if (attrs.email) {
      this.email = attrs.email;
    }
    if (attrs.phoneNumber) {
      this.phoneNumber = attrs.phoneNumber;
    }
    if (attrs.defaultPlatformId) {
      this.defaultPlatformId = attrs.defaultPlatformId;
    }
    return UserRepository.update({ dashboardUserId: this.id, ...attrs }, reCaptchaToken);
  }
}
